import { useState } from "react";
import type { GrammarLearnerLevel, InterfaceLocale, ReaderTheme } from "@readmf/contracts";

import { useI18n } from "./i18n";

export type AccountPreferences = {
  uiLocale?: InterfaceLocale;
  grammarLearnerLevel: GrammarLearnerLevel;
  theme: ReaderTheme;
};

export function AccountPage({
  uiLocale,
  grammarLearnerLevel,
  theme,
  signingOut,
  signOutError,
  onPreferencesChange,
  onSignOut,
  compact = false,
}: {
  compact?: boolean;
  uiLocale: InterfaceLocale;
  grammarLearnerLevel: GrammarLearnerLevel;
  theme: ReaderTheme;
  signingOut: boolean;
  signOutError: string | null;
  onPreferencesChange: (preferences: AccountPreferences) => Promise<void>;
  onSignOut: () => void;
}) {
  const { copy } = useI18n();
  const [saving, setSaving] = useState<"language" | "grammar" | "theme" | null>(null);
  const [preferenceError, setPreferenceError] = useState<string | null>(null);

  async function savePreferences(
    field: "language" | "grammar" | "theme",
    preferences: AccountPreferences,
  ): Promise<void> {
    setSaving(field);
    setPreferenceError(null);
    try {
      await onPreferencesChange(preferences);
    } catch {
      setPreferenceError(copy("Settings couldn’t be saved. Try again.", "Не удалось сохранить настройки. Попробуйте ещё раз."));
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className={compact ? "account-menu-controls" : "account-page"}>
      <section className={compact ? "account-menu-settings" : "account-settings"} aria-label={copy("Account settings", "Настройки аккаунта")}>
        <label className={compact ? "reader-option-section reader-select-option" : "account-setting-row account-setting-control"}>
          <span className={compact ? "reader-stepper-label" : undefined}>{compact ? copy("Language", "Язык") : copy("Interface and help language", "Язык интерфейса и подсказок")}</span>
          <select
            value={uiLocale}
            disabled={signingOut || (saving !== null && saving !== "language")}
            aria-busy={saving === "language"}
            aria-disabled={saving === "language"}
            aria-label={compact ? copy("Language", "Язык") : copy("Interface and help language", "Язык интерфейса и подсказок")}
            onChange={(event) => {
              if (saving !== null) return;
              void savePreferences("language", {
                uiLocale: event.target.value as InterfaceLocale,
                grammarLearnerLevel,
                theme,
              });
            }}
          >
            <option value="en">English</option>
            <option value="ru">Русский</option>
          </select>
        </label>
        <label className={compact ? "reader-option-section reader-select-option" : "account-setting-row account-setting-control"}>
          <span className={compact ? "reader-stepper-label" : undefined}>{copy("Grammar level", "Уровень грамматики")}</span>
          <select
            value={grammarLearnerLevel}
            disabled={signingOut || (saving !== null && saving !== "grammar")}
            aria-busy={saving === "grammar"}
            aria-disabled={saving === "grammar"}
            aria-label={copy("Grammar level", "Уровень грамматики")}
            onChange={(event) => {
              if (saving !== null) return;
              const nextLevel = event.target.value as GrammarLearnerLevel;
              void savePreferences("grammar", {
                grammarLearnerLevel: nextLevel,
                theme,
              });
            }}
          >
            {(["A1", "A2", "B1", "B2", "C1"] as const).map((level) => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </label>
        <label className={compact ? "reader-option-section reader-select-option" : "account-setting-row account-setting-control"}>
          <span className={compact ? "reader-stepper-label" : undefined}>{copy("Theme", "Тема")}</span>
          <select
            value={theme}
            disabled={signingOut || (saving !== null && saving !== "theme")}
            aria-busy={saving === "theme"}
            aria-disabled={saving === "theme"}
            aria-label={copy("Theme", "Тема")}
            onChange={(event) => {
              if (saving !== null) return;
              const nextTheme = event.target.value as ReaderTheme;
              void savePreferences("theme", {
                grammarLearnerLevel,
                theme: nextTheme,
              });
            }}
          >
            <option value="system">{copy("System", "Системная")}</option>
            <option value="light">{copy("Light", "Светлая")}</option>
            <option value="dark">{copy("Dark", "Тёмная")}</option>
          </select>
        </label>
        {saving ? <p className={compact ? "reader-option-status" : "account-save-status"} role="status">{copy("Saving…", "Сохраняем…")}</p> : null}
        {preferenceError ? <p className={compact ? "reader-option-error" : "account-error"} role="alert">{preferenceError}</p> : null}
      </section>

      <section className={compact ? "reader-option-section reader-book-edit-option" : "account-session"} aria-label={copy("Account session", "Сеанс аккаунта")}>
        <button
          type="button"
          aria-busy={signingOut}
          aria-disabled={signingOut || saving !== null}
          onClick={() => {
            if (!signingOut && saving === null) onSignOut();
          }}
        >
          {signingOut ? copy("Signing out…", "Выходим…") : copy("Sign out", "Выйти")}
        </button>
        {signOutError ? <p className={compact ? "reader-option-error" : "account-error"} role="alert">{signOutError}</p> : null}
      </section>
    </div>
  );
}
